import { Vec2 } from 'kaplay';
import { Colors, getAllColorsButOne, getRandomColor } from '../constants/colors';
import { getAllLanguagesButOne, getRandomLanguage } from '../constants/languages';
import { Tags } from '../constants/tags';
import { k } from '../main';
import { StatementType } from '../nls/statement-types';
import { getMessageForTone } from '../nls/statements';
import { GameState } from '../types/game-state';

// TODO: fix usages of any
export function addNpcCircle({
  pos,
  gameState,
}: {
  pos: Vec2;
  gameState: Pick<GameState, 'playerColor' | 'playerKnownLanguage'>;
}) {
  const color = rgb(...getRandomColor(getAllColorsButOne(gameState.playerColor)));
  const language = getRandomLanguage(getAllLanguagesButOne(gameState.playerKnownLanguage));

  const npcCircle = add([k.pos(pos), circle(32), area(), body(), Tags.NPC]);

  // Add tone level property to the NPC circle
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (npcCircle as any).toneLevel = 'random';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (npcCircle as any).language = language;

  npcCircle.onDraw(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentToneLevel = (npcCircle as any).toneLevel;

    if (currentToneLevel === 'frustrated') {
      drawCircle({
        color: rgb(...(Colors.BLACK as [number, number, number])),
        radius: 33,
      });
    }

    // Draw thicker black outline for angry NPCs
    if (currentToneLevel === 'angry') {
      drawCircle({
        color: rgb(...(Colors.BLACK as [number, number, number])),
        radius: 36, // Thicker outline
      });
    }

    drawCircle({
      color: rgb(...(Colors.BLACK as [number, number, number])),
      radius: 32,
    });
    drawCircle({
      color,
      radius: 30,
    });
  });

  return npcCircle;
}

/**
 * Creates the crowd of NPC circles.
 */
export function addNpcCrowd({
  sceneHeight,
  sceneWidth,
  gameState,
  numNpcs,
}: {
  sceneHeight: number;
  sceneWidth: number;
  gameState: Pick<GameState, 'playerColor' | 'playerKnownLanguage'>;
  numNpcs: number;
}) {
  const buffer = 100; // Buffer from the bottom

  const npcCircles: Array<ReturnType<typeof addNpcCircle>> = [];
  for (let i = 0; i < numNpcs; i++) {
    // Random position across the entire scene
    const randomX = Math.random() * sceneWidth;
    const randomY = Math.random() * (sceneHeight - buffer);
    const npcCircle = addNpcCircle({
      gameState,
      pos: vec2(randomX, randomY),
    });
    makeNpcMoveMultiDirectionally(npcCircle);
    npcCircles.push(npcCircle);
  }

  return npcCircles;
}

// Multi-directional NPC movement with tone-based behavior modifications
export function makeNpcMoveMultiDirectionally(npcCircle: ReturnType<typeof addNpcCircle>) {
  // update speed to be random between 50 and 100
  const baseSpeed = Math.random() * 50 + 50;
  let currentSpeed = baseSpeed;

  // Multi-directional movement: random direction vector
  let direction = vec2(
    (Math.random() - 0.5) * 2, // Random X direction between -1 and 1
    (Math.random() - 0.5) * 2, // Random Y direction between -1 and 1
  ).unit(); // Normalize to unit vector

  let isPaused = false;
  let isAvoidingNpc = false;

  // Tone level system
  let toneLevel: StatementType = 'random';
  let collisionCount = 0;
  let lastToneLowerTime = time();
  let isFollowingPlayer = false;
  let isAvoidingPlayer = false;

  npcCircle.onUpdate(() => {
    // Lower tone level every 4 seconds (except when angry)
    if (toneLevel !== 'angry' && time() - lastToneLowerTime > 4) {
      if (toneLevel === 'frustrated') toneLevel = 'annoyed';
      else if (toneLevel === 'annoyed') toneLevel = 'random';
      lastToneLowerTime = time();
      // Update the tone level property on the NPC circle for drawing
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (npcCircle as any).toneLevel = toneLevel;
    }

    if (isPaused) return;

    // Player following behavior (frustrated and angry)
    if (isFollowingPlayer) {
      const player = get(Tags.PLAYER)[0];
      if (player) {
        const followSpeed = toneLevel === 'angry' ? baseSpeed * 1.5 : baseSpeed * 0.8;
        const direction = player.pos.sub(npcCircle.pos).unit();
        npcCircle.move(direction.x * followSpeed, direction.y * followSpeed);
      }
      return;
    }

    // Player avoidance behavior (annoyed and frustrated)
    if (isAvoidingPlayer) {
      const player = get(Tags.PLAYER)[0];
      if (player) {
        const avoidDirection = npcCircle.pos.sub(player.pos).unit();
        // Frustrated NPCs move faster when avoiding than annoyed NPCs
        const avoidSpeed = toneLevel === 'frustrated' ? baseSpeed * 1.2 : baseSpeed;
        npcCircle.move(avoidDirection.x * avoidSpeed, avoidDirection.y * avoidSpeed);
      }
      return;
    }

    // NPC avoidance behavior is now handled in the onCollide callback
    // Just do normal multi-directional movement when not avoiding NPCs
    if (!isAvoidingNpc) {
      // Normal multi-directional movement
      npcCircle.move(direction.x * currentSpeed, direction.y * currentSpeed);
    }
    // Note: When isAvoidingNpc is true, movement is handled in the onCollide callback
  });

  // Handle player collision with tone level progression
  npcCircle.onCollide(Tags.PLAYER, () => {
    if (isPaused) return;

    collisionCount++;
    lastToneLowerTime = time(); // Reset tone lowering timer

    // Update tone level based on collision count
    if (collisionCount === 1) {
      toneLevel = 'random';
    } else if (collisionCount === 2) {
      toneLevel = 'annoyed';
      isAvoidingPlayer = true;
      isFollowingPlayer = false;
    } else if (collisionCount === 3) {
      toneLevel = 'frustrated';
      isAvoidingPlayer = true; // Frustrated NPCs also avoid player
      isFollowingPlayer = false;
    } else if (collisionCount >= 4) {
      toneLevel = 'angry';
      isAvoidingPlayer = false;
      isFollowingPlayer = true;
    }

    // Update the tone level property on the NPC circle for drawing
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (npcCircle as any).toneLevel = toneLevel;

    isPaused = true;

    // the message is calculated based upon the current tone, and the pre-existing calculated language
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const message = getMessageForTone(toneLevel, (npcCircle as any).language);
    // Show text above the circle with tone level
    const text = add([
      k.text(`${message}!`, { size: 16 }),
      pos(npcCircle.pos.x, npcCircle.pos.y - 50),
      color(0, 0, 0),
      anchor('center'),
    ]);

    wait(2, () => {
      isPaused = false;
      destroy(text); // Remove the text after 2 seconds
    });
  });

  // Change direction when hitting a wall (reflect off walls)
  npcCircle.onCollide(Tags.WALL, () => {
    // Get a new random direction when hitting a wall
    direction = vec2((Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2).unit();
  });

  // Improved NPC collision avoidance - move away from other NPCs
  npcCircle.onCollide(Tags.NPC, (otherNpc) => {
    if (!isAvoidingNpc && !isFollowingPlayer && !isAvoidingPlayer) {
      isAvoidingNpc = true;
    }

    // Calculate direction away from the other NPC
    const avoidDirection = npcCircle.pos.sub(otherNpc.pos).unit();
    // Move away from the other NPC
    npcCircle.move(avoidDirection.x * baseSpeed * 0.5, avoidDirection.y * baseSpeed * 0.5);
    currentSpeed = baseSpeed * 0.7; // Slightly reduce speed while avoiding
  });

  // Return to multi-directional movement when no longer colliding with NPCs
  npcCircle.onCollideEnd(Tags.NPC, () => {
    isAvoidingNpc = false;
    currentSpeed = baseSpeed; // Reset to original speed
    // Get a new random direction when collision ends
    direction = vec2((Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2).unit();
  });
}
